import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Globe, MapPin, ChevronRight, Loader2, Bell, Check, Clock, ArrowLeft, Home
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CountrySelector = ({ user }) => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState(user?.email || '');
  const [waitlistInterest, setWaitlistInterest] = useState('');
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  
  // Check if user already has a profession selected (can go back to dashboard)
  const canGoToDashboard = user?.selected_profession;

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries/`);
      setCountries(response.data.countries || []);
    } catch (error) {
      toast.error('Error cargando países');
    } finally {
      setLoading(false);
    }
  };

  const selectCountry = async (countryCode, available) => {
    if (!available) {
      setShowWaitlist(countryCode);
      return;
    }

    setSelecting(countryCode);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/countries/select`, 
        { country_code: countryCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local user
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      currentUser.selected_country = countryCode;
      currentUser.selected_profession = null;
      localStorage.setItem('user', JSON.stringify(currentUser));

      toast.success('País seleccionado');
      navigate('/select-profession');
    } catch (error) {
      toast.error('Error seleccionando país');
    } finally {
      setSelecting(null);
    }
  };

  const joinWaitlist = async () => {
    if (!waitlistEmail) {
      toast.error('Introduce tu email');
      return;
    }

    setJoiningWaitlist(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/countries/waitlist`, 
        { 
          country_code: showWaitlist, 
          email: waitlistEmail,
          profession_interest: waitlistInterest
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setShowWaitlist(null);
    } catch (error) {
      toast.error('Error al registrarte en la lista de espera');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country?.name || code;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Back to Dashboard button */}
            {canGoToDashboard && (
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver al Dashboard</span>
                <Home className="w-4 h-4 sm:hidden" />
              </Button>
            )}
            <div className={canGoToDashboard ? "" : "mx-auto"}>
              <OposaiLogo size={45} />
            </div>
            {canGoToDashboard && <div className="w-20" />}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            ¿Dónde quieres opositar?
          </h1>
          <p className="text-slate-600">
            Selecciona tu país para ver las oposiciones disponibles
          </p>
        </motion.div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {countries.map((country, index) => (
            <motion.div
              key={country.code}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <button
                onClick={() => selectCountry(country.code, country.available)}
                disabled={selecting === country.code}
                className={`w-full p-6 rounded-2xl border-2 transition-all text-left relative overflow-hidden group ${
                  country.available
                    ? 'bg-white border-slate-200 hover:border-indigo-400 hover:shadow-lg'
                    : 'bg-slate-50 border-slate-200 hover:border-amber-400'
                }`}
              >
                {/* Coming Soon Badge */}
                {country.coming_soon && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Próximamente
                    </span>
                  </div>
                )}

                {/* Flag & Name */}
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-4xl">{country.flag}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">{country.name}</h3>
                    <p className="text-sm text-slate-500">{country.regions_label}</p>
                  </div>
                </div>

                {/* Action */}
                <div className="flex items-center justify-between">
                  {country.available ? (
                    <>
                      <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Disponible
                      </span>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                    </>
                  ) : (
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Bell className="w-4 h-4" />
                      Avísame cuando esté disponible
                    </span>
                  )}
                </div>

                {/* Loading State */}
                {selecting === country.code && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Info */}
        <p className="text-center text-sm text-slate-500 mt-8">
          ¿No encuentras tu país? Estamos expandiendo constantemente.{' '}
          <a href="mailto:info@tuplazaia.com" className="text-indigo-600 hover:underline">
            Contáctanos
          </a>
        </p>
      </div>

      {/* Waitlist Modal */}
      {showWaitlist && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowWaitlist(null)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <span className="text-5xl mb-4 block">
                {countries.find(c => c.code === showWaitlist)?.flag}
              </span>
              <h3 className="text-xl font-bold text-slate-800">
                {getCountryName(showWaitlist)} - Próximamente
              </h3>
              <p className="text-slate-600 mt-2">
                Estamos preparando las oposiciones de {getCountryName(showWaitlist)}. 
                ¡Apúntate para ser de los primeros en enterarte!
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-600">Tu email</label>
                <Input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">¿Qué oposición te interesa? (opcional)</label>
                <Input
                  value={waitlistInterest}
                  onChange={(e) => setWaitlistInterest(e.target.value)}
                  placeholder="Ej: Policía, Enfermería, Docencia..."
                  className="mt-1"
                />
              </div>
              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={joinWaitlist}
                disabled={joiningWaitlist}
              >
                {joiningWaitlist ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Avisarme cuando esté disponible
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => setShowWaitlist(null)}>
                Cancelar
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
